import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { isNativePlatform } from '@/shared/utils/platform';

interface NotificationClickMessage {
  type: 'NOTIFICATION_CLICK';
  url: string;
}

/**
 * Hook to listen for web push notification clicks via postMessage from service worker
 * Service worker sends postMessage when navigate() is not supported
 */
export function useWebPushListeners() {
  const navigate = useNavigate();

  useEffect(() => {
    if (isNativePlatform()) {
      return;
    }

    const handleMessage = async (event: MessageEvent<unknown>) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as NotificationClickMessage | undefined;
      if (data?.type === 'NOTIFICATION_CLICK' && data.url) {
        try {
          // URL is already absolute from service worker
          const url = new URL(data.url);
          const path = url.pathname || '/';

          // Parse path and navigate with proper typed routes
          if (path.startsWith('/my-duos/')) {
            const id = path.split('/my-duos/')[1]?.split('/')[0];
            if (id) {
              await navigate({
                to: '/my-duos/$id',
                params: { id },
              });
            }
          } else if (path.startsWith('/duo-chats/')) {
            const duoId = path.split('/duo-chats/')[1]?.split('/')[0];
            if (duoId) {
              await navigate({
                to: '/duo-chats/$duoId',
                params: { duoId },
              });
            }
          } else if (path === '/likes') {
            await navigate({ to: '/likes' });
          } else {
            await navigate({ to: '/' });
          }
        } catch {
          // Ignore navigation errors
        }
      }
    };

    if (!navigator.serviceWorker) {
      return;
    }

    const serviceWorker = navigator.serviceWorker;
    serviceWorker.addEventListener('message', handleMessage);

    return () => {
      serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [navigate]);
}
